import * as React from 'react';
import { Link } from 'gatsby';
import { useIntl } from 'react-intl';
import { NavLinkProps } from './types';
import Dropdown from './assets/dropdown.inline.svg';
import * as styles from './NavLink.module.scss';

function normalizeInternalLink(url?: string) {
    if (!url) return { isInternal: false, path: '' };
    if (url.startsWith('/')) return { isInternal: true, path: url };
    const m = url.match(/^https?:\/\/(?:www\.)?(?:qm\.)?bondvet\.com(\/.*)?$/i);
    if (m) return { isInternal: true, path: m[1] || '/' };
    return { isInternal: false, path: url };
}

type NavAnchorProps = {
    path: string;
    isInternal: boolean;
    external?: boolean;
    tabIndex?: number;
    children: React.ReactNode;
    className?: string;
};

function NavAnchor({
    path,
    isInternal,
    external,
    tabIndex = 0,
    children,
    className = styles.root,
}: NavAnchorProps) {
    const common = { className, tabIndex, role: 'menuitem' as const };
    const intl = useIntl();

    if (external) {
        // open in new tab even for internal URLs
        return (
            <a
                href={path}
                {...common}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={intl.formatMessage({
                    id: 'nav.opensInNewTab',
                })}
            >
                {children}
            </a>
        );
    }

    return isInternal ? (
        // check if we can use Gatsby Link for internal links
        <Link to={path} {...common} activeClassName={styles.active}>
            {children}
        </Link>
    ) : (
        // an external link marked to open on the same tab
        <a href={path} {...common}>
            {children}
        </a>
    );
}

type SubmenuToggleProps = {
    onToggle?: () => void;
    tabIndex?: number;
    className?: string;
    children: React.ReactNode;
};

function SubmenuToggle({
    onToggle,
    tabIndex = 0,
    className = styles.root,
    children,
}: SubmenuToggleProps) {
    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        onToggle?.();
    };
    const handleKeyDown = (e: React.KeyboardEvent<HTMLAnchorElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle?.();
        }
    };

    return (
        /* eslint-disable jsx-a11y/anchor-is-valid */
        <a
            href="#"
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            className={className}
            role="button"
            aria-haspopup="true"
            aria-expanded="false"
            tabIndex={tabIndex}
        >
            {children}
        </a>
    );
}

export default function Content({
    to,
    label,
    subItems,
    tabIndex,
    toggleSubmenu,
    external,
}: NavLinkProps) {
    const { isInternal, path } = normalizeInternalLink(to);

    if (subItems && subItems.length > 0) {
        return (
            <>
                {Boolean(to) && to !== '/' ? (
                    <NavAnchor
                        path={path}
                        isInternal={isInternal}
                        external={external}
                        tabIndex={tabIndex}
                    >
                        {label}
                    </NavAnchor>
                ) : (
                    <SubmenuToggle onToggle={toggleSubmenu} tabIndex={tabIndex}>
                        {label}
                    </SubmenuToggle>
                )}

                <SubmenuToggle
                    onToggle={toggleSubmenu}
                    className={styles.iconButton}
                    tabIndex={tabIndex}
                >
                    <Dropdown className={styles.dropdown} />
                </SubmenuToggle>

                <ul className={styles.subMenu} role="menu">
                    {subItems.map((subItem) => (
                        <li key={subItem.to} role="menuitem">
                            <Content {...subItem} tabIndex={tabIndex} />
                        </li>
                    ))}
                </ul>
            </>
        );
    }

    // not a submenu
    return (
        <NavAnchor
            path={path}
            isInternal={isInternal}
            external={external}
            tabIndex={tabIndex}
        >
            {label}
        </NavAnchor>
    );
}
